/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON: a call toast with an action ("Try again"), which Element Call's
 * Toast does not have. Same look; non-modal, so the call stays usable.
 */

import { type FC, useCallback, useEffect } from "react";
import {
  Root as DialogRoot,
  Content as DialogContent,
  Close as DialogClose,
  Title as DialogTitle,
} from "@radix-ui/react-dialog";
import classNames from "classnames";
import { Button, Text } from "@vector-im/compound-web";

import toastStyles from "../Toast.module.css";
import overlayStyles from "../Overlay.module.css";
import styles from "./IonToast.module.css";

/** Without an action a toast stays 6 s, with one 10 s (design). */
export const TOAST_MS = 6000;
export const TOAST_WITH_ACTION_MS = 10_000;

interface Props {
  open: boolean;
  onDismiss: () => void;
  text: string;
  action?: { label: string; onClick: () => void };
}

export const IonToast: FC<Props> = ({ open, onDismiss, text, action }) => {
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onDismiss();
    },
    [onDismiss],
  );
  const hasAction = action !== undefined;
  useEffect(() => {
    if (!open) return;
    const timeout = setTimeout(
      onDismiss,
      hasAction ? TOAST_WITH_ACTION_MS : TOAST_MS,
    );
    return (): void => clearTimeout(timeout);
  }, [open, hasAction, onDismiss]);

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        aria-describedby={undefined}
        className={classNames(
          overlayStyles.overlay,
          overlayStyles.animate,
          toastStyles.toast,
          styles.toast,
        )}
      >
        <DialogClose className={styles.text}>
          <DialogTitle asChild>
            <Text as="h3" size="sm" weight="semibold">
              {text}
            </Text>
          </DialogTitle>
        </DialogClose>
        {action && (
          <Button
            kind="secondary"
            size="md"
            className={styles.action}
            onClick={(): void => {
              onDismiss();
              action.onClick();
            }}
          >
            {action.label}
          </Button>
        )}
      </DialogContent>
    </DialogRoot>
  );
};
